// src/hooks/useAnnotationToolTracking.ts
/**
 * Custom hook for tracking annotation tools when switching between tool modes
 * 
 * This hook ensures that when switching from annotation mode to another mode,
 * the current annotation tool is saved, and when switching back to annotation mode,
 * the previous annotation tool is restored.
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { 
  setActiveTool, 
  setPreviousAnnotationTool, 
  selectActiveTool,
  selectPreviousAnnotationTool 
} from '../store/slices/toolModeSlice';
import { setActiveTool as setUIActiveTool } from '../store/slices/uiSlice';

// Annotation tools that should be tracked
const ANNOTATION_TOOLS = [
  'select', 'text', 'equation', 'pen', 'highlighter', 
  'eraser', 'rectangle', 'rectangle-filled', 'circle', 'line'
] as const;

export function useAnnotationToolTracking() {
  const dispatch = useDispatch();
  const activeToolMode = useSelector(selectActiveTool);
  const previousAnnotationTool = useSelector(selectPreviousAnnotationTool);
  const currentAnnotationTool = useSelector((state: RootState) => state.ui.activeTool);

  // Track tool mode changes
  useEffect(() => {
    // This effect will run whenever activeToolMode changes
    // We need to handle the logic here since we can't use middleware easily
  }, [activeToolMode]);

  // Custom function to handle tool mode switching with annotation tool tracking
  const switchToolMode = (newToolMode: 'pan' | 'jump' | 'measurement' | 'annotation' | 'expression') => {
    // If switching from annotation mode to another mode, save the current annotation tool
    if (activeToolMode === 'annotation' && newToolMode !== 'annotation') {
      if (currentAnnotationTool && ANNOTATION_TOOLS.includes(currentAnnotationTool as any)) {
        dispatch(setPreviousAnnotationTool(currentAnnotationTool as any));
      }
      // Reset UI active tool to prevent interference with other tools
      dispatch(setUIActiveTool('select'));
    }

    // If switching to annotation mode from another mode, restore the previous annotation tool
    if (newToolMode === 'annotation' && activeToolMode !== 'annotation') {
      if (previousAnnotationTool) {
        dispatch(setUIActiveTool(previousAnnotationTool));
      } else {
        // Default to select tool if no previous annotation tool
        dispatch(setUIActiveTool('select'));
      }
    }

    // Switch the tool mode
    dispatch(setActiveTool(newToolMode));
  };

  return { switchToolMode };
} 